import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import TriviaCompletionModal from '../modals/TriviaCompletionModal';
import TriviaQuestionCard from '../Layout/TriviaQuestionCard';

export default function TriviaPage({ questions = [], onTriviaComplete }) {
    const navigate = useNavigate();

    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [showExplanation, setShowExplanation] = useState(false);
    const [showCompletion, setShowCompletion] = useState(false);

    const hasQuestions = questions.length > 0;
    const currentQuestion = questions[currentIndex];
    const isLastQuestion = currentIndex === questions.length - 1;
    const progress = hasQuestions ? ((currentIndex + 1) / questions.length) * 100 : 0;

    const handleSelect = (option) => {
        setSelectedOption(option);
    };

    const handleSubmit = () => {
        if (selectedOption) setShowExplanation(true);
    };

    const handleNext = () => {
        setSelectedOption(null);
        setShowExplanation(false);

        if (!isLastQuestion) {
            setCurrentIndex((prev) => prev + 1);
        } else {
            setShowCompletion(true);
            // Mark trivia as completed
            if (onTriviaComplete) {
                onTriviaComplete();
            }
        }
    };

    const handleCompletionClose = () => {
        setShowCompletion(false);
        navigate('/landing', { replace: true });
    };

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <div className="w-100 relative min-h-screen ">
            {/* SVG Background Pattern */}
            <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                viewBox="0 0 1200 600"
                preserveAspectRatio="xMidYMid slice"
            >
                <defs>
                    <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#2e7d32" strokeWidth="0.5" opacity="0.1" />
                    </pattern>
                    
                    <pattern id="dots" width="60" height="60" patternUnits="userSpaceOnUse">
                        <circle cx="30" cy="30" r="2" fill="#2e7d32" opacity="0.08" />
                    </pattern>

                    <linearGradient id="fadeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#f9fafb" stopOpacity="1" />
                        <stop offset="100%" stopColor="#f3f4f6" stopOpacity="1" />
                    </linearGradient>
                </defs>

                {/* Background gradient */}
                <rect width="1200" height="600" fill="url(#fadeGradient)" />
                
                {/* Grid pattern */}
                <rect width="1200" height="600" fill="url(#grid)" />
                
                {/* Dots pattern */}
                <rect width="1200" height="600" fill="url(#dots)" />
            </svg>

            {/* Header / Progress Bar */}
            <div className="shadow-sm sticky top-[6px] z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
                <div className="w-100 mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {/* Back Button */}
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleBack}
                            className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 transition-colors"
                            aria-label="Go back"
                        >
                            <svg className="w-5 h-5 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M19 12H5M12 19l-7-7 7-7" />
                            </svg>
                        </motion.button>

                        <div className="w-8 h-8 rounded-full bg-[#2e7d32]/10 flex items-center justify-center">
                            <svg className="w-5 h-5 text-[#2e7d32]" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2c-1 0-2 1-2 2v1c0 .55.45 1 1 1s1-.45 1-1V4c0-.55.45-1 1-1s1 .45 1 1v1c0 .55.45 1 1 1s1-.45 1-1V4c0-1-1-2-2-2z" />
                                <path d="M6 6c-1.1 0-2 .9-2 2v3h2v-3c0-.55.45-1 1-1h10c.55 0 1 .45 1 1v3h2V8c0-1.1-.9-2-2-2H6z" />
                                <path d="M4 11h2v7c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2v-7h2v7c0 2.2-1.8 4-4 4H8c-2.2 0-4-1.8-4-4v-7z" />
                            </svg>
                        </div>
                        <h1 className="text-lg font-semibold text-gray-800">Daily Health Scenarios</h1>
                    </div>

                    {hasQuestions && (
                        <div className="text-sm text-gray-500">
                            {currentIndex + 1} / {questions.length}
                        </div>
                    )}
                </div>

                {/* Thin progress bar */}
                {hasQuestions && (
                    <div className="h-1 bg-gray-100">
                        <motion.div
                            className="h-full bg-[#2e7d32]"
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 0.4 }}
                        />
                    </div>
                )}
            </div>

            <main className="relative z-0 max-w-3xl mx-auto px-4 py-6 md:py-10">
                {!hasQuestions ? (
                    // Empty state
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6"
                    >
                        <div className="w-24 h-24 rounded-full bg-[#2e7d32]/5 flex items-center justify-center mb-6">
                            <svg className="w-24 h-24 text-[#2e7d32] opacity-40" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2c-5.33 4.55-8 6.75-8 11 0 4.42 3.58 8 8 8s8-3.58 8-8c0-4.25-2.67-6.45-8-11z" />
                            </svg>
                        </div>

                        <h2 className="text-2xl font-semibold text-gray-800 mb-3">
                            No new scenario today
                        </h2>

                        <p className="text-gray-600 max-w-md mb-8 leading-relaxed">
                            You've already completed today's health scenario or new questions are still being prepared.
                            Check back tomorrow for fresh knowledge!
                        </p>

                        <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => navigate('/landing')}
                            className="px-8 py-3 bg-[#2e7d32] text-white rounded-full font-medium shadow-sm hover:shadow-md transition-all"
                        >
                            Return to Home
                        </motion.button>

                        <p className="mt-10 text-sm text-gray-400">
                            Your next set refreshes daily at midnight
                        </p>
                    </motion.div>
                ) : (
                    // Active trivia questions
                    <motion.div
                        key={currentIndex}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                        className="relative"
                    >
                        <TriviaQuestionCard
                            question={currentQuestion}
                            selectedOption={selectedOption}
                            showExplanation={showExplanation}
                            onSelectOption={handleSelect}
                            onSubmit={handleSubmit}
                            onNext={handleNext}
                            isLastQuestion={isLastQuestion}
                        />
                    </motion.div>
                )}
            </main>

            {/* Completion Modal */}
            {showCompletion && (
                <TriviaCompletionModal onClose={handleCompletionClose} />
            )}
        </div>
    );
}